import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  InputSignal,
  OnChanges,
  OnDestroy,
  OnInit,
  signal,
  SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import { NetworkHostsSummaryDirection } from '@uigy/generated/graphql';
import type { NetworkSummaryQuery } from '@uigy/generated/graphql';
import {
  ConversationPoint,
  TrafficAndConverstionsChart,
  TrafficPoint,
} from '@uigy/modules/network-summary/models/interfaces';
import {
  NetworkSummaryService,
  TimeFrameWithRange,
} from '@uigy/modules/network-summary/services/network-summary.service';
import { COLORS } from '@uigy/shared/constants/colors.constant';
import { DatesFormatsEnum } from '@uigy/shared/enums/dateFormats.enum';
import { RoutesStatesNamesEnum } from '@uigy/shared/enums/routesStatesNames.enum';
import { DateTimeService } from '@uigy/shared/services/date-time/date-time.service';
import { LanguageService } from '@uigy/shared/services/language.service';
import Utils from '@uigy/utils';
import { AgChartsAngular } from 'ag-charts-angular';
import { AgCartesianChartOptions, AgCartesianSeriesTooltipRendererParams } from 'ag-charts-community';
import { Subscription, tap } from 'rxjs';
import {
  NetworkSummaryDataHelperService
} from '@uigy/modules/network-summary/services/networkSummaryDataHelper.service';
import { maxBy } from 'lodash-es';

@Component({
    selector: 'app-network-graph',
    imports: [
    AgChartsAngular
],
    templateUrl: './network-graph.component.html',
    styleUrl: './network-graph.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class NetworkGraphComponent implements OnInit, OnDestroy, OnChanges {
  private networkSummaryService = inject(NetworkSummaryService);
  private networkSummaryDataHelperService = inject(NetworkSummaryDataHelperService);
  protected readonly languageService = inject(LanguageService);

  private router = inject(Router);
  protected readonly Utils = Utils;
  public readonly texts = this.languageService.texts.overview.network_summary;

  public unit: string = '';
  public options?: AgCartesianChartOptions;
  private exponent: number = 0;
  public loading = signal(false);
  public count = signal(0);
  public timeFrame: InputSignal<TimeFrameWithRange> = input.required();
  private readonly _crossLinesColor = Utils.getCssVariableValue('--border-primary');
  private timeFrameSubscription?: Subscription;
  public isLaptopScreen = signal<boolean>(false);
  private readonly LARGE_SCREEN = Number(Utils.getCssVariableValue('--full-hdpi-size').replace('px', ''));
  ngOnInit(): void {
    if (this.timeFrame()) {
      this.getNetworkData();
    }
    this.isLaptopScreen.set(window.innerWidth < this.LARGE_SCREEN);
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.timeFrame && this.timeFrame()) {
      this.getNetworkData();
    }
  }
  public ngOnDestroy(): void {
    if (this.timeFrameSubscription) {
      this.timeFrameSubscription.unsubscribe();
    }
  }
  private getNetworkData(): void {
    const currentTimeFrame = this.timeFrame();
    if (currentTimeFrame) {
      this.loading.set(true);
      this.networkSummaryService.getNetworkHostsCount(currentTimeFrame.value, NetworkHostsSummaryDirection.Destination)
        .pipe(
          tap((networkHostCount) => {
            this.count.set(networkHostCount?.series.totalCount ?? 0);
          })).subscribe();
      this.timeFrameSubscription = this.networkSummaryService.getNetworkSummary(
        currentTimeFrame.value,
      ).pipe(
        tap((networkSummary: NetworkSummaryQuery['networkSummary'] | null) => {
          if (networkSummary) {
            this.setCorrectUnit(networkSummary); // compute exponent & unit before formatting/tooltip
            this.options = this.setOptions(networkSummary);
          }
          this.loading.set(false);
        })
      ).subscribe();
    }
  }

  private getTooltip(color: COLORS, amount: string, label: string): string {
    return `
      <div class="widget-tooltip-container">
        <div  class="widget-tooltip-circle" style="background-color: ${color};"></div>
        <div class="widget-tooltip-text" >
          <strong>${amount}</strong> ${label}
        </div>
      </div>
    `;
  }

  private setOptions(networkSummary: NetworkSummaryQuery['networkSummary']): AgCartesianChartOptions {
    const formattedNetworkSummary = this.reformatSeries(networkSummary);
    // Calculate min and max with padding
    const trafficYValues = formattedNetworkSummary.traffic.map(point => point.trafficY);
    const trafficYMin = Math.min(...trafficYValues);
    const trafficYMax = Math.max(...trafficYValues);
    const trafficYPadding = (trafficYMax - trafficYMin) * 0.1;
    const trafficYPMid = (trafficYMax + trafficYMin) / 2;
    const conYValues = formattedNetworkSummary.conversations.map(point => point.conY);
    const conYMin = Math.min(...conYValues);
    const conYMax = Math.max(...conYValues);
    const conYPadding = (conYMax - conYMin) * 0.1;
    return {
      autoSize: true,
      padding: {
        top: 0,
        right: 0,
        bottom: 5,
        left: 0,
      },
      background: {
        fill: 'transparent',
      },
      series: [
        {
          data: formattedNetworkSummary.traffic,
          type: 'line',
          xKey: 'trafficX',
          yKey: 'trafficY',
          stroke: COLORS.azureSkies0,
          strokeWidth: 1,
          marker: {
            enabled: false,
          },
          tooltip: {
            renderer: (params: AgCartesianSeriesTooltipRendererParams): any => {
              // Tooltip uses pre-scaled datum value with shared unit (matching traffic-and-conversations component)
              return this.getTooltip(
                COLORS.azureSkies0,
                `${params.datum.trafficY} ${this.unit}`,
                this.languageService.texts.network.network_summary.conversation_traffic_spline_chart.traffic_legend
              );
            }
          },
          highlightStyle: {
            series: {
              enabled: false,
            },
          },
        },
        {
          data: formattedNetworkSummary.conversations,
          type: 'line',
          xKey: 'conX',
          yKey: 'conY',
          stroke: COLORS.azureSkies1,
          strokeWidth: 1,
          marker: {
            enabled: false,
          },
          tooltip: {
            renderer: (params: AgCartesianSeriesTooltipRendererParams): any => {
              const conversationTexts = this.languageService.texts.network.network_summary.conversation_traffic_spline_chart;
              return this.getTooltip(COLORS.azureSkies1, Utils.humanizeNumber(this.languageService.texts.general.not_available, params.datum.conY), conversationTexts.conversations_legend);
            }
          },
          highlightStyle: {
            series: {
              enabled: false,
            },
          },
        },
      ],
      axes: [
        {
          position: 'bottom',
          type: 'time',
          nice: false,
          line: {
            width: 0,
            color: 'transparent',
          },
          label: {
            fontFamily: 'Inter',
            fontSize: 12,
            color: Utils.getCssVariableValue('--text-secondary'),
            formatter: (params): string => {
              let dateFormat = DatesFormatsEnum.hourMinutes;
              const unit = this.timeFrame()?.unit ?? '';
              if ((unit === 'day' && this.timeFrame().amount && this.timeFrame().amount > 4) || ['week', 'month'].includes(unit)) {
                dateFormat = DatesFormatsEnum.monthDay;
              }
              return DateTimeService.utcToLocal(params.value).format(dateFormat);
            },
            padding: -10
          },
          gridLine: {
            style: [{
              stroke: this._crossLinesColor,
              lineDash: [2, 2],
            }],
          }
        },
        {
          type: 'number',
          position: 'left',
          keys: ['trafficY'],
          min: trafficYMin - trafficYPadding,
          max: trafficYMax + trafficYPadding,
          line: {
            width: 0,
            color: 'transparent',
          },
          tick: {
            width: 0,
            size: 0,
            color: 'transparent',
          },
          label: {
            enabled: false,
          },
          gridLine: {
            style: [],
          },
          crossLines: [{
            type: 'line',
            value: trafficYPMid,
            stroke: this._crossLinesColor,
            lineDash: [2, 2],
          }]
        },
        {
          type: 'number',
          position: 'right',
          keys: ['conY'],
          min: conYMin - conYPadding,
          max: conYMax + conYPadding,
          line: {
            width: 0,
            color: 'transparent',
          },
          tick: {
            width: 0,
            size: 0,
            color: 'transparent',
          },
          label: {
            enabled: false,
          },
          gridLine: {
            style: [],
          }
        },
      ],
      legend: {
        enabled: false,
      },
    };
  }

  private reformatSeries(networkSummary: NetworkSummaryQuery['networkSummary']): TrafficAndConverstionsChart {
    // we need to create a JavaScript Date that ag-Grid can understand.
    const traffic: TrafficPoint[] = (Utils.stripConnection(networkSummary.sizeSeries) as Array<{x: string; y: number}>).map((traffic): TrafficPoint => ({
        trafficX: DateTimeService.utcToLocal(traffic.x).toDate(),
        trafficY: +(traffic.y / Math.pow(1024, this.exponent)).toFixed(2),
      })),
      conversations: ConversationPoint[] = (Utils.stripConnection(networkSummary.conversationsSeries) as Array<{x: string; y: number}>).map((conversation): ConversationPoint => ({
        conX: DateTimeService.utcToLocal(conversation.x).toDate(),
        conY: conversation.y,
      }));
    return { traffic: traffic, conversations: conversations };
  }

  private setCorrectUnit(networkSummary: NetworkSummaryQuery['networkSummary']): void {
    const max: number = maxBy(Utils.stripConnection(networkSummary.sizeSeries) as Array<{y: number}>, 'y')?.y ?? 0;
    this.exponent = this.networkSummaryDataHelperService.getExponent(max);
    this.unit = this.networkSummaryDataHelperService.getCorrectUnit(max);
  }

  public handleClick(): void {
    this.router.navigate([RoutesStatesNamesEnum.network, RoutesStatesNamesEnum.networkSummary]);
  }
}
